import { useTable, useSelect } from "@refinedev/antd";
import { useGetIdentity, useList } from "@refinedev/core";
import { Card, Table, Space, Button, Input, App, Tag, Select } from "antd";
import {
  AuditOutlined,
  ClearOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import type { Audit } from "../../types";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { usePageTitle } from "../../hooks";
import { ADMIN_USER_TYPES, type UserInfo } from "../../types/auth";
import {
  getAuditDisplayState,
  colorForAuditDisplayState,
} from "../../utils/auditDisplayState";

interface VarietyChip {
  id: number;
  code: string;
  name: string;
}

interface AuditRow extends Audit {
  /** Solo admin lo recibe poblado. */
  surface_review_status?: string | null;
  auditor_company_name?: string | null;
  varieties?: VarietyChip[];
  /** Refine no tipa estos campos extra; declaramos lo que usamos. */
  season_name?: string | null;
}

// El color y el label del estado vienen del helper compartido
// (auditDisplayState), que también desglosa Planificada en
// 'Sin asignar' / 'Asignada' / 'Planificada' según auditor_user +
// planning_date.

// Año actual + 5 atrás y 1 adelante para el Select de campaña.
const CURRENT_YEAR = new Date().getFullYear();
const SEASON_YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => {
  const y = CURRENT_YEAR + 1 - i;
  return { label: String(y), value: y };
});

const SURFACE_REVIEW_META: Record<
  string,
  { color: string; label: string }
> = {
  ok: { color: "green", label: "OK" },
  reviewed: { color: "green", label: "Aprobada" },
  pending_review: { color: "gold", label: "Pendiente" },
  outdated: { color: "orange", label: "Re-validar" },
  irregularity: { color: "red", label: "Irregularidad" },
  no_data: { color: "default", label: "Sin snapshot" },
};

const SURFACE_REVIEW_OPTIONS = [
  { text: "Pendiente revisión", value: "pending" },
  { text: "Irregularidad", value: "irregularity" },
  { text: "Ya revisadas", value: "reviewed" },
  { text: "OK (Δ = 0)", value: "ok" },
];

export const AuditList = () => {
  const { t } = useTranslation();
  const { message: _msg } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();

  // Si volvemos del detalle con un scrollY capturado, restauramos tras
  // un tick (para que la tabla termine de pintar su altura). El state
  // se consume con history.replaceState para evitar re-scrolls en F5.
  useEffect(() => {
    const restoreScroll = (location.state as any)?.restoreScroll;
    if (typeof restoreScroll === "number") {
      const t = setTimeout(() => {
        window.scrollTo({ top: restoreScroll, behavior: "auto" });
      }, 80);
      window.history.replaceState({}, "");
      return () => clearTimeout(t);
    }
  }, [location.state]);

  usePageTitle("Auditorías");
  const [searchText, setSearchText] = useState("");

  const { data: identity } = useGetIdentity<UserInfo>();
  const isAdmin = (ADMIN_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );

  const { tableProps, filters, setFilters } = useTable<AuditRow>({
    resource: "audits",
    sorters: {
      initial: [{ field: "created", order: "desc" }],
    },
    pagination: { pageSize: 50 },
    syncWithLocation: true,
  });

  const { selectProps: auditTypeSelectProps } = useSelect({
    resource: "audit-types",
    optionLabel: "name",
    optionValue: "id",
  });
  const { selectProps: auditStateSelectProps } = useSelect({
    resource: "audit-states",
    optionLabel: "name",
    optionValue: "id",
  });
  // PR6 — Opciones para los dropdown filters de provincia y municipio.
  // Trae el catálogo completo (≤500 entries); se cachea por Refine
  // con staleTime alto para no re-fetchear en cada navegación.
  // 'v1/...' es el prefijo que el dataProvider espera para endpoints
  // que solo viven bajo /api/v1/.
  const { result: provinceResult } = useList<{ id: number; name: string }>({
    resource: "v1/provinces",
    pagination: { mode: "off" },
    queryOptions: { staleTime: 10 * 60 * 1000 },
  });
  const { result: townResult } = useList<{ id: number; name: string }>({
    resource: "v1/towns",
    pagination: { mode: "off" },
    queryOptions: { staleTime: 10 * 60 * 1000 },
  });
  const provinceFilterOptions = useMemo(
    () =>
      (provinceResult?.data ?? [])
        .map((p: any) => ({ text: p.name, value: p.id }))
        .sort((a, b) => a.text.localeCompare(b.text)),
    [provinceResult?.data],
  );
  const townFilterOptions = useMemo(
    () =>
      (townResult?.data ?? [])
        .map((t: any) => ({ text: t.name, value: t.id }))
        .sort((a, b) => a.text.localeCompare(b.text)),
    [townResult?.data],
  );

  /* ------------------------------------------------------------------ */
  /* Filter handling — single-select column filters wired into Refine.  */
  /* ------------------------------------------------------------------ */

  const getFilterValue = (field: string): string | number | undefined => {
    const f = filters?.find((x) => "field" in x && x.field === field) as
      | { value?: any }
      | undefined;
    return f?.value;
  };

  const updateFilter = (field: string, value: any) => {
    const next = filters?.filter(
      (x) => !("field" in x) || x.field !== field,
    ) || [];
    if (value !== undefined && value !== null && value !== "") {
      next.push({ field, operator: "eq" as const, value });
    }
    setFilters(next, "replace");
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    const next = filters?.filter(
      (x) => !("field" in x) || x.field !== "q",
    ) || [];
    if (value && value.trim() !== "") {
      next.push({ field: "q", operator: "contains" as const, value: value.trim() });
    }
    setFilters(next, "replace");
  };

  const handleClearFilters = () => {
    setSearchText("");
    setFilters([], "replace");
  };

  const hasActiveFilters =
    (filters && filters.length > 0) || searchText !== "";

  // Options for column-level dropdowns. antd Table.Column.filters
  // expects [{text, value}].
  const typeFilterOptions = useMemo(
    () =>
      (auditTypeSelectProps.options || []).map((o: any) => ({
        text: String(o.label),
        value: o.value,
      })),
    [auditTypeSelectProps.options],
  );
  const stateFilterOptions = useMemo(
    () =>
      (auditStateSelectProps.options || []).map((o: any) => ({
        text: String(o.label),
        value: o.value,
      })),
    [auditStateSelectProps.options],
  );

  return (
    <Card className="audits-list">
      <PageHeader
        icon={<AuditOutlined />}
        accent="#722ED1"
        title={t("audit.list_title", "Auditorías")}
        subtitle={t(
          "audit.list_subtitle",
          "Listado de auditorías realizadas y planificadas",
        )}
      />

      {/* Toolbar: búsqueda + Select multiple de campaña + limpiar.
          Tipo, Estado y Revisión sup. se filtran en las cabeceras de
          columna. */}
      <Space
        wrap
        style={{ justifyContent: "space-between", width: "100%", marginBottom: 8 }}
      >
        <Space wrap>
          <Input.Search
            placeholder={t(
              "audit.search",
              "Buscar por productor, finca, referencia, provincia...",
            )}
            allowClear
            enterButton={<SearchOutlined />}
            size="middle"
            style={{ width: 420 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
          />
          <Select
            mode="multiple"
            options={SEASON_YEAR_OPTIONS}
            placeholder={t("audit.season", "Campaña")}
            allowClear
            maxTagCount="responsive"
            // Mismo criterio que en /planning-groups: input ancho para
            // ver los chips, popup aún más ancho para los nombres
            // completos de campaña.
            style={{ minWidth: 320 }}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 360 }}
            value={(() => {
              // El valor en filters está como CSV ('2025,2026') →
              // lo convertimos a array para el Select.
              const raw = getFilterValue("season_year");
              if (raw == null || raw === "") return [];
              return String(raw)
                .split(",")
                .map((s) => Number(s))
                .filter((n) => Number.isFinite(n));
            })()}
            onChange={(values: number[]) => {
              // BaseInFilter del backend espera CSV: '2025,2026'.
              if (!values || values.length === 0) {
                updateFilter("season_year", undefined);
              } else {
                updateFilter("season_year", values.join(","));
              }
            }}
          />
        </Space>
        {hasActiveFilters && (
          <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
            {t("common.clearFilters", "Limpiar filtros")}
          </Button>
        )}
      </Space>

      {/* Contador total tras filtros — lee tableProps.pagination.total. */}
      {(() => {
        const total =
          typeof tableProps.pagination === "object" && tableProps.pagination
            ? (tableProps.pagination as any).total
            : undefined;
        if (total == null) return null;
        return (
          <div style={{ marginBottom: 8 }}>
            <Tag color="blue">
              {total.toLocaleString("es-ES")}{" "}
              {total === 1 ? "auditoría" : "auditorías"}
            </Tag>
            <span style={{ marginLeft: 6, fontSize: 12, color: "#8c8c8c" }}>
              tras filtros
            </span>
          </div>
        );
      })()}

      <Table
        {...tableProps}
        rowKey="id"
        className="planning-audits-table"
        size="small"
        scroll={{ x: 1500 }}
        onRow={(record: AuditRow) => ({
          onClick: (event) => {
            const target = event.target as HTMLElement;
            if (target.closest("button") || target.closest("a")) return;
            // Snapshot del listado para que el botón Volver del detalle
            // restaure URL (filtros + página + sort que Refine sincroniza
            // con la URL) + scrollY del listado.
            navigate(`/audits/${record.id}`, {
              state: {
                from: "/audits",
                listSnapshot: {
                  search: location.search || "",
                  scrollY: window.scrollY,
                },
              },
            });
          },
          style: { cursor: "pointer" },
        })}
        onChange={(pagination, antdFilters, sorter, extra) => {
          // Captura los dropdown filters de columna (tipo, estado,
          // surface_review, campaña) y reenvía al onChange de Refine.
          const pickFirst = (raw: any): any => {
            if (Array.isArray(raw) && raw.length > 0) return raw[0];
            return undefined;
          };
          const nextType = pickFirst((antdFilters as any)?.audit_type_name);
          const nextState = pickFirst((antdFilters as any)?.audit_state_name);
          const nextReview = pickFirst(
            (antdFilters as any)?.surface_review_status,
          );
          const nextProvince = pickFirst((antdFilters as any)?.province_name);
          const nextTown = pickFirst((antdFilters as any)?.town_name);
          if (nextType !== getFilterValue("audit_type")) {
            updateFilter("audit_type", nextType);
          }
          if (nextState !== getFilterValue("audit_state")) {
            updateFilter("audit_state", nextState);
          }
          if (isAdmin && nextReview !== getFilterValue("surface_review_filter")) {
            updateFilter("surface_review_filter", nextReview);
          }
          if (nextProvince !== getFilterValue("province")) {
            updateFilter("province", nextProvince);
          }
          if (nextTown !== getFilterValue("town")) {
            updateFilter("town", nextTown);
          }
          (tableProps as any)?.onChange?.(pagination, antdFilters, sorter, extra);
        }}
      >
        <Table.Column
          dataIndex="producer_name"
          title={t("audit.producer", "Productor")}
          sorter
          width={160}
          ellipsis
        />
        <Table.Column
          dataIndex="farm_name"
          title={t("audit.farm", "Finca")}
          sorter
          width={140}
          ellipsis
        />
        <Table.Column
          dataIndex="reference"
          title={t("audit.reference", "Referencia")}
          sorter
          width={110}
        />
        <Table.Column
          key="province_name"
          dataIndex="province_name"
          title={t("audit.province", "Provincia")}
          sorter
          width={140}
          filterMultiple={false}
          filterSearch
          filteredValue={
            getFilterValue("province") != null
              ? [getFilterValue("province") as any]
              : null
          }
          filters={provinceFilterOptions}
          onFilter={() => true}
          render={(v: string | null) => v || "—"}
        />
        <Table.Column
          key="town_name"
          dataIndex="town_name"
          title={t("audit.town", "Municipio")}
          sorter
          width={150}
          ellipsis
          filterMultiple={false}
          filterSearch
          filteredValue={
            getFilterValue("town") != null
              ? [getFilterValue("town") as any]
              : null
          }
          filters={townFilterOptions}
          onFilter={() => true}
          render={(v: string | null) => v || "—"}
        />
        <Table.Column
          dataIndex="audit_type_name"
          title={t("audit.type", "Tipo")}
          width={130}
          filterMultiple={false}
          filteredValue={
            getFilterValue("audit_type") != null
              ? [getFilterValue("audit_type") as any]
              : null
          }
          filters={typeFilterOptions}
          onFilter={() => true}
          render={(v: string | null) =>
            v ? <Tag color="cyan">{v}</Tag> : "—"
          }
        />
        {/* Fecha va antes que Estado: a los coordinadores les es más
            útil escanear "cuándo" antes que "en qué estado" porque la
            columna Estado ya se refleja con color de etiqueta visible
            de un vistazo. */}
        <Table.Column
          dataIndex="start_date"
          title={t("audit.start_date", "Fecha")}
          sorter
          width={110}
          render={(v: string | null) =>
            v ? dayjs(v).format("DD/MM/YYYY") : "—"
          }
        />
        <Table.Column
          dataIndex="audit_state_name"
          title={t("audit.state", "Estado")}
          width={140}
          filterMultiple={false}
          filteredValue={
            getFilterValue("audit_state") != null
              ? [getFilterValue("audit_state") as any]
              : null
          }
          filters={stateFilterOptions}
          onFilter={() => true}
          render={(_: unknown, record: AuditRow) => {
            const display = getAuditDisplayState(record);
            if (!display)
              return <span style={{ color: "#bfbfbf" }}>—</span>;
            return (
              <Tag color={colorForAuditDisplayState(display)}>{display}</Tag>
            );
          }}
        />
        {/* Columna 'Campaña' eliminada — el filtro vive ahora en la
            toolbar como Select multiple. */}
        {isAdmin && (
          <Table.Column
            dataIndex="surface_review_status"
            title="Revisión sup."
            width={150}
            filterMultiple={false}
            filteredValue={
              getFilterValue("surface_review_filter") != null
                ? [getFilterValue("surface_review_filter") as any]
                : null
            }
            filters={SURFACE_REVIEW_OPTIONS}
            onFilter={() => true}
            render={(v: string | null) => {
              if (!v) return <span style={{ color: "#bfbfbf" }}>—</span>;
              const m = SURFACE_REVIEW_META[v] ?? {
                color: "default",
                label: v,
              };
              return <Tag color={m.color}>{m.label}</Tag>;
            }}
          />
        )}
        <Table.Column
          dataIndex="varieties"
          title="Variedades"
          width={180}
          // Sort cliente por la concatenación alfabética. Agrupa filas
          // con la misma variedad principal sin necesidad de añadir
          // ordering server-side.
          sorter={(a: AuditRow, b: AuditRow) => {
            const al = (a.varieties ?? []).map((v) => v.name).join(", ");
            const bl = (b.varieties ?? []).map((v) => v.name).join(", ");
            return al.localeCompare(bl);
          }}
          render={(items: VarietyChip[] | undefined) => {
            if (!items || items.length === 0)
              return <span style={{ color: "#bfbfbf" }}>—</span>;
            return (
              <Space size={4} wrap>
                {items.map((v) => (
                  <Tag key={v.id} color="blue" style={{ margin: 0 }}>
                    {v.name}
                  </Tag>
                ))}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="auditor_name"
          title={t("audit.auditor", "Auditor")}
          sorter
          width={150}
          ellipsis
          render={(v: string | null) => v || "—"}
        />
        <Table.Column
          dataIndex="auditor_company_name"
          title={t("audit.auditor_company", "Empresa auditora")}
          width={160}
          ellipsis
          render={(v: string | null) => v || "—"}
        />
      </Table>
    </Card>
  );
};
